/**
 * AI Quiz Analysis Service - Phân tích kết quả bài tập trắc nghiệm bằng AI
 * Tích hợp OpenAI GPT để đưa ra nhận xét về điểm mạnh, điểm yếu và đề xuất cải thiện
 */

import { BankQuestion } from '../bankQuestionService_moi'
import { QuestionAnalysis, ScoringResult } from '../quiz/QuizScoringService'
import aiUsageService from './AIUsageService'
import { AIUsageReservation } from '../../types/ai'
import { API_CONFIG } from '../../config/api.config'
import { aiHeaders, hasAIKey, handleAIError, openAIConfigDialog } from './aiConfig'

export interface AIQuizAnalysis {
  success: boolean
  isFallback?: boolean

  // Nhận xét tổng quan
  overallFeedback: string

  // Điểm mạnh của học sinh
  strengths: {
    title: string
    description: string
    examples: string[]
  }[]

  // Điểm yếu cần cải thiện
  weaknesses: {
    title: string
    description: string
    affectedQuestions: number[]
  }[]

  // Đề xuất nội dung lý thuyết cần học
  theoryRecommendations: {
    topic: string
    priority: 'high' | 'medium' | 'low'
    reason: string
    chapters: string[]
  }[]

  // Đề xuất bài tập cần làm
  exerciseRecommendations: {
    type: string
    difficulty: 'M1' | 'M2' | 'M3'
    quantity: number
    reason: string
  }[]

  // Lời khuyên cụ thể
  specificAdvice: string[]

  error?: string
  tokensUsed?: number
}

class AIQuizAnalysisService {
  private model: string = 'gpt-4o-mini'
  private maxTokens: number = 2048
  private temperature: number = 0.7

  constructor() {
    // Phân tích quiz đi qua backend + khoá AI của người dùng (xem services/ai/aiConfig).
  }

  /**
   * Phân tích kết quả quiz bằng AI
   */
  async analyzeQuizResults(
    scoringResult: ScoringResult,
    questions: BankQuestion[],
    lessonId?: string,
    options?: { userId?: string; silent?: boolean }
  ): Promise<AIQuizAnalysis> {
    try {
      // Kiểm tra cấu hình
      // Chưa nhập khoá AI → trả phân tích rút gọn thay vì báo lỗi giữa bài làm.
      // silent = tự chạy sau khi nộp bài: KHÔNG bật hộp thoại nhập khoá (HS không có khoá bị hỏi mỗi lần nộp).
      if (!hasAIKey()) {
        if (!options?.silent) openAIConfigDialog()
        return this.createFallbackAnalysis(scoringResult, questions)
      }

      let usageReservation: AIUsageReservation | null = null
      if (options?.userId) {
        try {
          usageReservation = await aiUsageService.consume({
            userId: options.userId,
            feature: 'quiz_analysis',
            metadata: {
              lessonId,
              totalQuestions: scoringResult.detailedAnalysis.length
            }
          })
        } catch (usageError) {
          console.warn('⚠️ AI Usage tracking failed (likely CORS or network error). Proceeding anyway...', usageError)
          // Don't block analysis if tracking fails
          usageReservation = null
        }
      }


      // Tạo prompt cho AI
      const prompt = this.createAnalysisPrompt(scoringResult, questions, lessonId)

      console.log('🤖 Sending quiz analysis request to AI...')

      // Gửi request tới OpenAI
      const response = await this.sendAIRequest(prompt)

      if (!response.success) {
        console.error('❌ AI analysis failed:', response.error)
        if (usageReservation && options?.userId) {
          aiUsageService.revert(usageReservation.usageId, options.userId).catch(err => {
            console.warn('Không thể hoàn lại lượt phân tích AI:', err)
          })
        }
        return this.createFallbackAnalysis(scoringResult, questions)
      }

      // Parse kết quả từ AI
      const analysis = this.parseAIResponse(response.content, scoringResult)

      console.log('✅ AI analysis completed successfully')

      return {
        ...analysis,
        tokensUsed: response.tokensUsed
      }

    } catch (error) {
      console.error('❌ Error in AI quiz analysis:', error)
      return this.createFallbackAnalysis(scoringResult, questions)
    }
  }

  /**
   * Tạo prompt chi tiết cho AI
   */
  private createAnalysisPrompt(
    scoringResult: ScoringResult,
    questions: BankQuestion[],
    lessonId?: string
  ): string {
    const { score, maxScore, percentage, detailedAnalysis } = scoringResult

    // Phân loại câu hỏi theo kết quả
    const correctQuestions = detailedAnalysis.filter(a => a.isCorrect)
    const incorrectQuestions = detailedAnalysis.filter(a => !a.isCorrect)

    // Thống kê theo loại câu hỏi
    const questionTypeStats = this.calculateQuestionTypeStats(detailedAnalysis)

    // Thống kê theo mức độ
    const difficultyStats = this.calculateDifficultyStats(detailedAnalysis)

    // Tạo thông tin chi tiết về các câu sai
    const incorrectQuestionsInfo = incorrectQuestions.map((analysis, index) => {
      const question = questions.find(q => q.id === analysis.questionId)
      return {
        number: index + 1,
        difficulty: analysis.difficulty,
        type: analysis.questionType,
        question: question?.cau_hoi?.substring(0, 100) + '...' || 'N/A',
        userAnswer: analysis.userAnswer,
        correctAnswer: analysis.correctAnswer
      }
    })

    const prompt = `Bạn là một chuyên gia giáo dục Vật lí với nhiều năm kinh nghiệm. Nhiệm vụ của bạn là phân tích kết quả bài kiểm tra trắc nghiệm của học sinh và đưa ra nhận xét, đánh giá chi tiết, cùng đề xuất cải thiện cụ thể.

THÔNG TIN BÀI KIỂM TRA:
- Điểm số: ${score}/${maxScore} (${percentage}%)
- Tổng số câu: ${detailedAnalysis.length}
- Số câu đúng: ${correctQuestions.length}
- Số câu sai: ${incorrectQuestions.length}
${lessonId ? `- Bài học: ${lessonId}` : ''}

THỐNG KÊ THEO LOẠI CÂU HỎI:
${Object.entries(questionTypeStats).map(([type, stats]) =>
      `- ${type}: ${stats.correct}/${stats.total} đúng (${Math.round(stats.correct / stats.total * 100)}%)`
    ).join('\n')}

THỐNG KÊ THEO MỨC ĐỘ:
${Object.entries(difficultyStats).map(([difficulty, stats]) =>
      `- ${difficulty}: ${stats.correct}/${stats.total} đúng (${Math.round(stats.correct / stats.total * 100)}%)`
    ).join('\n')}

CHI TIẾT CÁC CÂU SAI (${incorrectQuestions.length} câu):
${incorrectQuestionsInfo.map(q =>
      `Câu ${q.number}: [${q.difficulty}] ${q.type}\n  Câu hỏi: ${q.question}\n  Trả lời: ${JSON.stringify(q.userAnswer)}\n  Đáp án: ${JSON.stringify(q.correctAnswer)}`
    ).join('\n\n')}

YÊU CẦU PHÂN TÍCH:
Hãy phân tích kết quả trên và trả lời theo định dạng JSON chính xác sau (KHÔNG thêm markdown, KHÔNG thêm backticks):

{
  "overallFeedback": "Nhận xét tổng quan về kết quả bài kiểm tra (2-3 câu)",
  "strengths": [
    {
      "title": "Tên điểm mạnh",
      "description": "Mô tả chi tiết về điểm mạnh này",
      "examples": ["Ví dụ cụ thể 1", "Ví dụ cụ thể 2"]
    }
  ],
  "weaknesses": [
    {
      "title": "Tên điểm yếu",
      "description": "Mô tả chi tiết về điểm yếu này",
      "affectedQuestions": [số thứ tự các câu bị ảnh hưởng]
    }
  ],
  "theoryRecommendations": [
    {
      "topic": "Chủ đề lý thuyết cần học",
      "priority": "high|medium|low",
      "reason": "Lý do cần học chủ đề này",
      "chapters": ["Chương 1", "Chương 2"]
    }
  ],
  "exerciseRecommendations": [
    {
      "type": "Loại bài tập cần làm",
      "difficulty": "M1|M2|M3",
      "quantity": số_lượng_bài_tập,
      "reason": "Lý do cần làm loại bài tập này"
    }
  ],
  "specificAdvice": [
    "Lời khuyên cụ thể 1",
    "Lời khuyên cụ thể 2",
    "Lời khuyên cụ thể 3"
  ]
}

LƯU Ý:
- Nhận xét phải cụ thể, dựa trên dữ liệu thống kê thực tế
- Ưu tiên phân tích các điểm yếu rõ ràng (các loại câu hỏi hoặc mức độ có tỷ lệ sai cao)
- Đề xuất lý thuyết phải liên quan trực tiếp đến các câu sai
- Đề xuất bài tập phải phù hợp với mức độ và loại câu hỏi học sinh còn yếu
- Lời khuyên phải khả thi và động viên học sinh

Hãy trả lời NGAY BÂY GIỜ chỉ bằng JSON thuần túy, KHÔNG có markdown, KHÔNG có \`\`\`json:`

    return prompt
  }

  /**
   * Tính thống kê theo loại câu hỏi
   */
  private calculateQuestionTypeStats(analysis: QuestionAnalysis[]): Record<string, { total: number, correct: number }> {
    const stats: Record<string, { total: number, correct: number }> = {}

    analysis.forEach(a => {
      const type = this.translateQuestionType(a.questionType)
      if (!stats[type]) {
        stats[type] = { total: 0, correct: 0 }
      }
      stats[type].total++
      if (a.isCorrect) {
        stats[type].correct++
      }
    })

    return stats
  }

  /**
   * Tính thống kê theo mức độ
   */
  private calculateDifficultyStats(analysis: QuestionAnalysis[]): Record<string, { total: number, correct: number }> {
    const stats: Record<string, { total: number, correct: number }> = {}

    analysis.forEach(a => {
      const difficulty = a.difficulty || 'Không xác định'
      if (!stats[difficulty]) {
        stats[difficulty] = { total: 0, correct: 0 }
      }
      stats[difficulty].total++
      if (a.isCorrect) {
        stats[difficulty].correct++
      }
    })

    return stats
  }

  /**
   * Dịch loại câu hỏi sang tiếng Việt
   */
  private translateQuestionType(type: string): string {
    const translations: Record<string, string> = {
      'trac_nghiem_nhieu_lua_chon': 'Trắc nghiệm nhiều lựa chọn',
      'multiple_choice': 'Trắc nghiệm nhiều lựa chọn',
      'dung_sai': 'Đúng/Sai',
      'true_false': 'Đúng/Sai',
      'tinh_toan': 'Tính toán',
      'short_answer': 'Trả lời ngắn'
    }
    return translations[type] || type
  }

  /**
   * Gửi request tới OpenAI API (tự động chọn proxy hoặc direct)
   */
  private async sendAIRequest(prompt: string): Promise<{
    success: boolean
    content?: string
    error?: string
    tokensUsed?: number
  }> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 60000) // 60s timeout

    try {
      // Luôn đi qua backend của mình, kèm khoá AI của người dùng. KHÔNG còn nhánh gọi
      // thẳng api.openai.com bằng khoá nhúng trong bundle (khoá đó ai cũng đọc được).
      const url = `${API_CONFIG.python.apiUrl}/ai/explain`
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...aiHeaders('text'),
      }
      const authToken = localStorage.getItem('auth_token')
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`

      // Chuẩn bị payload cho OpenAI API
      const payload = {
        model: this.model,
        messages: [
          {
            role: 'system',
            content: 'Bạn là chuyên gia giáo dục Vật lí. Trả lời CHÍNH XÁC theo định dạng JSON được yêu cầu, KHÔNG thêm markdown hay backticks.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: this.temperature,
        max_tokens: this.maxTokens
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({} as any))
        handleAIError(response.status)   // 428: mở hộp thoại Cấu hình AI
        const detail = errorData.detail || errorData.error?.message || 'Unknown error'
        throw new Error(`AI Error: ${response.status} - ${detail}`)
      }

      const data = await response.json()
      const content = data.choices?.[0]?.message?.content || ''

      return {
        success: true,
        content,
        tokensUsed: data.usage?.total_tokens || 0
      }

    } catch (error) {
      clearTimeout(timeoutId)

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return {
            success: false,
            error: 'Yêu cầu quá thời gian chờ'
          }
        }
        return {
          success: false,
          error: error.message
        }
      }

      return {
        success: false,
        error: 'Có lỗi xảy ra khi kết nối với AI'
      }
    }
  }

  /**
   * Parse response từ AI
   */
  private parseAIResponse(content: string, scoringResult: ScoringResult): AIQuizAnalysis {
    try {
      // Loại bỏ markdown code blocks nếu có
      let cleanContent = content.trim()
      if (cleanContent.startsWith('```json')) {
        cleanContent = cleanContent.replace(/```json\n?/, '').replace(/\n?```$/, '')
      } else if (cleanContent.startsWith('```')) {
        cleanContent = cleanContent.replace(/```\n?/, '').replace(/\n?```$/, '')
      }

      const parsed = JSON.parse(cleanContent)

      return {
        success: true,
        isFallback: false,
        overallFeedback: parsed.overallFeedback || 'Không có nhận xét',
        strengths: parsed.strengths || [],
        weaknesses: parsed.weaknesses || [],
        theoryRecommendations: parsed.theoryRecommendations || [],
        exerciseRecommendations: parsed.exerciseRecommendations || [],
        specificAdvice: parsed.specificAdvice || []
      }

    } catch (error) {
      console.error('❌ Error parsing AI response:', error)
      console.log('Raw content:', content)

      // Fallback nếu không parse được
      return this.createFallbackAnalysis(scoringResult, [])
    }
  }

  /**
   * Tạo phân tích fallback khi AI không khả dụng
   */
  private createFallbackAnalysis(
    scoringResult: ScoringResult,
    questions: BankQuestion[]
  ): AIQuizAnalysis {
    const { percentage, detailedAnalysis } = scoringResult

    const correctCount = detailedAnalysis.filter(a => a.isCorrect).length
    const incorrectCount = detailedAnalysis.filter(a => !a.isCorrect).length

    // Phân tích cơ bản
    const strengths = []
    const weaknesses = []
    const theoryRecommendations = []
    const exerciseRecommendations = []

    // Xác định điểm mạnh
    if (percentage >= 80) {
      strengths.push({
        title: 'Kiến thức vững vàng',
        description: 'Bạn đã nắm vững phần lớn kiến thức trong bài học này',
        examples: ['Trả lời đúng nhiều câu hỏi', 'Hiểu rõ các khái niệm cơ bản']
      })
    }

    // Xác định điểm yếu
    if (incorrectCount > 0) {
      const incorrectQuestionIndices = detailedAnalysis
        .map((a, i) => !a.isCorrect ? i + 1 : -1)
        .filter(i => i !== -1)

      weaknesses.push({
        title: 'Một số câu hỏi còn sai sót',
        description: `Bạn đã làm sai ${incorrectCount} câu hỏi. Hãy xem lại lời giải chi tiết.`,
        affectedQuestions: incorrectQuestionIndices
      })
    }

    // Đề xuất lý thuyết
    if (percentage < 70) {
      theoryRecommendations.push({
        topic: 'Ôn tập lại lý thuyết cơ bản',
        priority: 'high' as const,
        reason: 'Kết quả chưa đạt yêu cầu, cần học lại từ đầu',
        chapters: ['Xem lại nội dung bài học']
      })
    } else if (incorrectCount > 0) {
      theoryRecommendations.push({
        topic: 'Củng cố kiến thức',
        priority: 'medium' as const,
        reason: 'Một số phần còn chưa vững, cần ôn lại',
        chapters: ['Xem lại các phần liên quan đến câu sai']
      })
    }

    // Đề xuất bài tập
    if (incorrectCount > 0) {
      exerciseRecommendations.push({
        type: 'Bài tập tương tự',
        difficulty: 'M1' as const,
        quantity: Math.min(incorrectCount * 2, 10),
        reason: 'Rèn luyện thêm các dạng bài còn yếu'
      })
    }

    const specificAdvice = [
      'Xem lại lời giải chi tiết cho từng câu hỏi',
      'Ghi chép lại các công thức và khái niệm quan trọng',
      'Làm thêm bài tập để củng cố kiến thức'
    ]

    if (percentage < 50) {
      specificAdvice.unshift('Nên học lại toàn bộ lý thuyết từ đầu')
    }

    return {
      success: true,
      isFallback: true,
      overallFeedback: this.generateBasicFeedback(percentage, correctCount, incorrectCount),
      strengths,
      weaknesses,
      theoryRecommendations,
      exerciseRecommendations,
      specificAdvice
    }
  }

  /**
   * Tạo feedback cơ bản
   */
  private generateBasicFeedback(percentage: number, correctCount: number, incorrectCount: number): string {
    if (percentage >= 90) {
      return `Xuất sắc! Bạn đã trả lời đúng ${correctCount} câu, thể hiện sự nắm vững kiến thức rất tốt.`
    } else if (percentage >= 80) {
      return `Tốt lắm! Bạn đã làm đúng ${correctCount} câu. Hãy tiếp tục duy trì và cải thiện thêm.`
    } else if (percentage >= 70) {
      return `Khá tốt! Bạn đã đạt yêu cầu với ${correctCount} câu đúng. Cố gắng cải thiện các câu còn sai.`
    } else if (percentage >= 50) {
      return `Cần cải thiện! Bạn đã làm đúng ${correctCount} câu nhưng còn ${incorrectCount} câu sai. Hãy xem lại lý thuyết.`
    } else {
      return `Cần học lại! Bạn mới chỉ làm đúng ${correctCount}/${correctCount + incorrectCount} câu. Hãy xem lại toàn bộ lý thuyết và luyện tập thêm.`
    }
  }
}

// Export singleton instance
export const aiQuizAnalysisService = new AIQuizAnalysisService()
export default aiQuizAnalysisService

